import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { DatePicker, InputNumber } from 'antd';
import { ShiftTypesRadioInput } from '@jobs/components';
import { getRateByEmploymentType } from '@institutions/services';
import { validateIfDateIsBeforeAnother } from 'works/legacy/services/date-interval-validator-service';
import { WorkforceGroupSelect } from '@scheduling/components';
import { BillingCardDeviationAlert } from 'works/legacy/components/billing-card-deviation-alert';
import { isNumber } from 'works/legacy/lib/number-helper';
import {
  Banner,
  CommentInput,
  CurrencyInput,
  InfoTooltip,
  Gutter,
  Form,
  Col,
  Row,
  Input,
} from '@common/components';
import { OvertimeThresholdSelect } from '@timekeeping/components';

const endDateFormName = 'endDate';

// The contract end date derived from a start date and the staffing request's
// length in weeks. Clones rather than adding in place: moment mutates, and
// both call sites are handed a value that stays live in the form, so adding in
// place would walk the start date forward on every validation pass.
//
// Cloning also keeps this agnostic about which date library the caller uses.
// Re-parsing via `moment(startDate)` would not: a dayjs instance is not a
// shape moment recognizes, so it silently falls back to now — which is what
// this helper would start doing the moment its callers hand it a DatetimeType.
//
// The start date can be absent. `validateEndDate` reads it from the form, where
// `initialOfferStartDate` leaves null once the request's own start date has
// elapsed and an untouched field reads undefined. `moment(startDate)` used to
// absorb both into an invalid instant whose comparisons were all false; a bare
// `.clone()` would throw instead, and antd surfaces a thrown validator message
// to the user. Null is returned rather than undefined on purpose: a comparison
// against null is false, but `isBefore(undefined)` measures from now, so
// undefined would trade the crash for a silently wrong validation.
export const endDateForStartDate = (startDate, lengthInWeeks) =>
  startDate ? startDate.clone().add(lengthInWeeks, 'weeks') : null;

export function OfferFormDetails({
  form,
  onFinish,
  onChange = () => {},
  application,
  initialValues = {},
  offerType = 'create',
  setIsFormValid,
}) {
  const staffingRequest = application.staffingRequest;
  const organizationSetting = application.organization.organizationSetting;
  const { billingCard } = staffingRequest;
  const hasBillingCard = !!billingCard;
  const isOnDemand = staffingRequest.staffingRequestType === 'on_demand';
  const [isDeviated, setIsDeviated] = useState(!hasBillingCard);
  const [currentRate, setCurrentRate] = useState(
    staffingRequest.billingRegularRate
  );
  const [showEndDateErrorAlert, setShowEndDateErrorAlert] = useState(false);
  const [showEndDateInfoAlert, setShowEndDateInfoAlert] = useState(false);
  const [showIncreaseRateError, setShowIncreaseRateError] = useState(false);
  const [showDeviationWarning, setShowDeviationWarning] = useState(false);

  function handleDeviation({ billingRegularRate }) {
    if (!hasBillingCard || !isNumber(billingRegularRate)) return;

    const notEqualBillingCardRate = billingRegularRate !== currentRate;
    const isDeviating = hasBillingCard && notEqualBillingCardRate;

    setIsDeviated(isDeviating);
    setShowDeviationWarning(isDeviating);
  }

  function handleCurrentRate({ shiftType }) {
    if (!shiftType) return;

    const currentBillingCardRate = getRateByEmploymentType(
      billingCard,
      shiftType,
      application.employmentType
    );

    if (hasBillingCard) setCurrentRate(currentBillingCardRate);

    if (hasBillingCard && !isDeviated) {
      form.setFieldsValue({ billingRegularRate: currentBillingCardRate });
    }
  }

  function handleOnValuesChange(values) {
    handleCurrentRate(values);
    handleDeviation(values);
    onChange(values);
  }

  function handleOnFinish(values) {
    onFinish({
      ...values,
      isDeviated,
    });
  }

  const activateErrorAlertOnly = () => {
    setShowEndDateInfoAlert(false);
    setShowEndDateErrorAlert(true);
    setIsFormValid(false);
  };

  const activateInfoAlertOnly = () => {
    setShowEndDateInfoAlert(true);
    setShowEndDateErrorAlert(false);
    setIsFormValid(true);
  };

  const deactivateAlerts = () => {
    setShowEndDateErrorAlert(false);
    setShowEndDateInfoAlert(false);
    setIsFormValid(true);
  };

  const handleStartDateChange = (value) => {
    if (!value) return;

    form.setFields([
      {
        name: endDateFormName,
        value: endDateForStartDate(value, staffingRequest.length),
      },
    ]);
    form.validateFields(['endDate']);
  };

  const handleEndDateChange = (value) => {
    if (!value) return;

    form.validateFields(['startDate']);
  };

  const validateEndDate = (_field, currentDate) => {
    const canExtend = application.canExtendOfferWeekAssignment.value;
    const startDate = form.getFieldValue('startDate');
    const startDateWithWeekAssignmentAddition = endDateForStartDate(
      startDate,
      staffingRequest.length
    );
    const isBeforeThanStartDate = currentDate?.isBefore(startDate);
    const isEndDateShorterThanItShould = currentDate?.isBefore(
      startDateWithWeekAssignmentAddition
    );
    const isEndDateLongerThanItShould = currentDate?.isAfter(
      startDateWithWeekAssignmentAddition
    );

    if (isEndDateShorterThanItShould || isEndDateLongerThanItShould)
      activateInfoAlertOnly();

    if (isBeforeThanStartDate || (!canExtend && isEndDateLongerThanItShould)) {
      activateErrorAlertOnly();
      return Promise.reject(
        isBeforeThanStartDate
          ? 'End date must be after Start Date.'
          : 'Cannot exceed requested length.'
      );
    }

    if (currentDate?.isSame(startDateWithWeekAssignmentAddition)) {
      deactivateAlerts();
    }

    return Promise.resolve();
  };

  const validateBillingRate = (_, value) => {
    const newValueHigherThanCurrentRate = value > currentRate;
    const hasPermissionToIncreaseRate = application.canIncreaseOfferRate.value;

    if (
      value &&
      newValueHigherThanCurrentRate &&
      !hasPermissionToIncreaseRate
    ) {
      setShowIncreaseRateError(true);
      return Promise.reject('Must be lower than specified.');
    }

    setShowIncreaseRateError(false);
    return Promise.resolve();
  };

  return (
    <div className="OfferFormDetails">
      <Form
        layout="vertical"
        form={form}
        onFinish={handleOnFinish}
        onValuesChange={handleOnValuesChange}
        initialValues={initialValues}
      >
        {isOnDemand && (
          <Row gutter={[8, 8]}>
            <Col span={24}>
              <WorkforceGroupSelect />
            </Col>
          </Row>
        )}

        <Row gutter={[8, 8]}>
          <Col lg={{ span: 18 }}>
            <Row gutter={[8, 8]} align="middle">
              <Col xs={{ span: 24 }} lg={{ span: 12 }}>
                <Form.Item
                  label="Start date"
                  name="startDate"
                  rules={[
                    { required: true, message: 'Please enter a start date' },
                    {
                      validator: validateIfDateIsBeforeAnother(
                        form,
                        'endDate',
                        'Start date must be before End date'
                      ),
                    },
                  ]}
                >
                  <DatePicker
                    name="Start Date"
                    placeholder="mm/dd/yyyy"
                    format="MM/DD/YYYY"
                    onChange={handleStartDateChange}
                  />
                </Form.Item>
              </Col>
              <Col xs={{ span: 24 }} lg={{ span: 12 }}>
                <div className="end-date-details">
                  <Form.Item
                    label="End date"
                    name={endDateFormName}
                    rules={[
                      { required: true, message: 'Please enter an end date' },
                      { validator: validateEndDate },
                    ]}
                  >
                    <DatePicker
                      name="End Date"
                      placeholder="mm/dd/yyyy"
                      format="MM/DD/YYYY"
                      onChange={handleEndDateChange}
                    />
                  </Form.Item>
                  <InfoTooltip
                    size="md"
                    leftOffset="lg"
                    title="End Date is automatically updated to match the length on the original request."
                  />
                </div>
              </Col>
            </Row>
          </Col>
          <Col lg={{ span: 6 }}>
            <Row gutter={[8, 8]}>
              {offerType === 'create' && (
                <Col xs={{ span: 24 }}>
                  <Form.Item
                    label="Rate"
                    name="billingRegularRate"
                    rules={[
                      { required: true, message: 'Please enter a rate' },
                      {
                        pattern: /^[1-9]\d*(\.\d+)?$/,
                        message: 'Rate must be at least $1',
                      },
                      {
                        validator: validateBillingRate,
                      },
                    ]}
                  >
                    <CurrencyInput
                      disabled={!staffingRequest.canUpdate.value}
                    />
                  </Form.Item>
                </Col>
              )}
            </Row>
          </Col>
        </Row>

        {showEndDateErrorAlert && (
          <Gutter direction="bottom" size="md">
            <Banner type="error">
              Length in weeks cannot be higher. Contact your organization admin
              to make this adjustment.
            </Banner>
          </Gutter>
        )}

        {showEndDateInfoAlert && (
          <Gutter direction="bottom" size="md">
            <Banner type="info">
              Length in weeks is different than the length on the original
              request.
            </Banner>
          </Gutter>
        )}

        {showIncreaseRateError && (
          <Gutter direction="bottom" size="md">
            <Banner type="error">
              You can&apos;t adjust the rate up. Contact your organization admin
              to get this rate raised.
            </Banner>
          </Gutter>
        )}

        {!showIncreaseRateError && showDeviationWarning && (
          <Gutter direction="bottom" size="md">
            <BillingCardDeviationAlert />
          </Gutter>
        )}

        <Form.Item
          label="Time Off"
          name="timeoffDescription"
          rules={[{ required: true, message: 'Please enter a time off' }]}
        >
          <Input />
        </Form.Item>

        <ShiftTypesRadioInput />

        <Row gutter={[8, 8]}>
          <Col span={12}>
            <Form.Item
              label="Shifts per week"
              name="shiftsPerWeek"
              rules={[
                {
                  required: true,
                  message: 'Please enter the amount of shifts per week',
                },
                {
                  type: 'number',
                  min: isOnDemand ? 0 : 1,
                  max: 999,
                  message: 'Shifts per week must be positive',
                },
              ]}
            >
              <InputNumber />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label="Hours per shift"
              name="hoursPerShift"
              rules={[
                {
                  required: true,
                  message: 'Please enter the amount of hours per shift',
                },
                {
                  type: 'number',
                  min: isOnDemand ? 0 : 1,
                  max: 999,
                  message: 'Hours per shift must be positive',
                },
              ]}
            >
              <InputNumber />
            </Form.Item>
          </Col>
        </Row>

        {organizationSetting?.timekeepingEnabled && <OvertimeThresholdSelect />}

        <CommentInput />
      </Form>
      <style jsx>
        {`
          .OfferFormDetails {
            margin-top: 1em;
          }
          .OfferFormDetails :global(.ant-input-number),
          .OfferFormDetails :global(.ant-form-item-control-input-content),
          .OfferFormDetails :global(.ant-picker) {
            width: 100%;
          }
          .OfferFormDetails :global(.link-icon) {
            margin-left: 8px;
            font-size: 11px;
          }
          .OfferFormDetails :global(.help-link:hover) {
            text-decoration: underline;
          }
          .OfferFormDetails :global(.ant-alert) {
            margin-top: 0.5rem;
          }
          .OfferFormDetails :global(.ant-form-item-explain) {
            margin-top: 2px;
            font-size: 11px;
          }

          .OfferFormDetails .end-date-details {
            display: flex;
            align-items: center;
          }

          .OfferFormDetails :global(.ant-row .ant-form-item) {
            width: 100%;
          }
        `}
      </style>
    </div>
  );
}

OfferFormDetails.propTypes = {
  form: PropTypes.object.isRequired,
  application: PropTypes.shape({
    id: PropTypes.string.isRequired,
    employmentType: PropTypes.string.isRequired,
    canExtendOfferWeekAssignment: PropTypes.shape({
      value: PropTypes.bool.isRequired,
    }),
    canIncreaseOfferRate: PropTypes.shape({
      value: PropTypes.bool.isRequired,
    }),
    organization: PropTypes.shape({
      organizationSetting: PropTypes.shape({
        timekeepingEnabled: PropTypes.bool.isRequired,
      }),
    }).isRequired,
    staffingRequest: PropTypes.shape({
      startDate: PropTypes.string.isRequired,
      endDate: PropTypes.string.isRequired,
      billingRegularRate: PropTypes.number.isRequired,
      shiftType: PropTypes.string.isRequired,
      shiftsPerWeek: PropTypes.number.isRequired,
      hoursPerShift: PropTypes.number.isRequired,
      staffingRequestType: PropTypes.string.isRequired,
      canUpdate: PropTypes.shape({
        value: PropTypes.bool.isRequired,
      }),
      billingCard: PropTypes.shape({
        id: PropTypes.string.isRequired,
      }),
      length: PropTypes.number.isRequired,
    }),
    timeoffDescription: PropTypes.string,
    worksProfile: PropTypes.shape({ id: PropTypes.string.isRequired }),
  }),
  onFinish: PropTypes.func,
  offerType: PropTypes.string,
  onChange: PropTypes.func,
  initialValues: PropTypes.object,
  displayDates: PropTypes.bool,
  setIsFormValid: PropTypes.func.isRequired,
};
